import React, { useEffect, useState, useCallback } from 'react';
import { Trophy, Plus, Pencil, Trash2, Check, X, Loader2, Settings } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import { championshipsApi } from '../lib/api';

const inputCls =
  'bg-[#0a0d15] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#FFD700]/50 w-full';

export const AlboLegacyManager = ({ open, onOpenChange, onChanged, isItalian }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [newWins, setNewWins] = useState(1);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editWins, setEditWins] = useState(1);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await championshipsApi.alboLegacyList();
      setEntries(res.data?.entries || []);
    } catch {
      toast.error(isItalian ? 'Errore caricamento voci storiche' : 'Failed to load legacy entries');
    } finally {
      setLoading(false);
    }
  }, [isItalian]);

  useEffect(() => {
    if (open) {
      load();
      setEditId(null);
      setConfirmDelete(null);
    }
  }, [open, load]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await championshipsApi.alboLegacyCreate({ club_name: newName.trim(), wins: Number(newWins) || 1 });
      toast.success(isItalian ? 'Voce storica aggiunta' : 'Legacy entry added');
      setNewName('');
      setNewWins(1);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isItalian ? 'Errore creazione' : 'Create failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (entryId) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await championshipsApi.alboLegacyUpdate(entryId, { club_name: editName.trim(), wins: Number(editWins) || 1 });
      toast.success(isItalian ? 'Voce aggiornata' : 'Entry updated');
      setEditId(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isItalian ? 'Errore modifica' : 'Update failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    setSaving(true);
    try {
      await championshipsApi.alboLegacyDelete(entryId);
      toast.success(isItalian ? 'Voce eliminata' : 'Entry deleted');
      setConfirmDelete(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isItalian ? 'Errore eliminazione' : 'Delete failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0d1020] border border-[#FFD700]/20 text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="albo-legacy-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-widest text-base flex items-center gap-2">
            <Settings size={18} className="text-[#FFD700]" />
            {isItalian ? "Gestione Trofei Storici" : 'Manage Legacy Trophies'}
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            {isItalian
              ? "Voci storiche dell'Albo d'Oro precedenti alla piattaforma. Se il nome coincide con un club esistente, i trofei vengono sommati."
              : 'Historical Hall of Fame entries pre-dating the platform. Names matching existing clubs get merged.'}
          </DialogDescription>
        </DialogHeader>

        {/* Add new entry */}
        <div className="p-3 bg-[#FFD700]/5 border border-[#FFD700]/20 rounded-xl space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-[#FFD700] font-bold flex items-center gap-1.5">
            <Plus size={12} />
            {isItalian ? 'Aggiungi voce storica' : 'Add legacy entry'}
          </p>
          <div className="flex gap-2">
            <input
              className={inputCls}
              placeholder={isItalian ? 'Nome club' : 'Club name'}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              data-testid="albo-legacy-new-name"
            />
            <input
              className={`${inputCls} !w-20 text-center`}
              type="number"
              min={1}
              value={newWins}
              onChange={(e) => setNewWins(e.target.value)}
              data-testid="albo-legacy-new-wins"
            />
            <button
              className="px-3 py-2 bg-[#FFD700]/15 border border-[#FFD700]/40 text-[#FFD700] rounded-lg text-sm font-bold hover:bg-[#FFD700]/25 transition-colors disabled:opacity-40 shrink-0"
              onClick={handleCreate}
              disabled={saving || !newName.trim()}
              data-testid="albo-legacy-add-btn"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            </button>
          </div>
        </div>

        {/* Entries list */}
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-slate-400 text-sm">
            <Loader2 size={16} className="animate-spin" />
            {isItalian ? 'Caricamento…' : 'Loading…'}
          </div>
        ) : entries.length === 0 ? (
          <p className="text-center text-slate-500 text-sm py-6" data-testid="albo-legacy-empty">
            {isItalian ? 'Nessuna voce storica presente' : 'No legacy entries yet'}
          </p>
        ) : (
          <div className="divide-y divide-white/5 border border-white/10 rounded-xl overflow-hidden">
            {entries.map((e) => (
              <div
                key={e.entry_id}
                className="flex items-center gap-2 px-3 py-2.5 bg-[#0a0d15]/60"
                data-testid={`albo-legacy-row-${e.entry_id}`}
              >
                {editId === e.entry_id ? (
                  <>
                    <input
                      className={inputCls}
                      value={editName}
                      onChange={(ev) => setEditName(ev.target.value)}
                      data-testid="albo-legacy-edit-name"
                    />
                    <input
                      className={`${inputCls} !w-16 text-center`}
                      type="number"
                      min={1}
                      value={editWins}
                      onChange={(ev) => setEditWins(ev.target.value)}
                      data-testid="albo-legacy-edit-wins"
                    />
                    <button
                      className="p-1.5 text-emerald-400 hover:bg-emerald-400/10 rounded-lg transition-colors disabled:opacity-40"
                      onClick={() => handleUpdate(e.entry_id)}
                      disabled={saving}
                      data-testid="albo-legacy-save-btn"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      className="p-1.5 text-slate-400 hover:bg-white/10 rounded-lg transition-colors"
                      onClick={() => setEditId(null)}
                      data-testid="albo-legacy-cancel-btn"
                    >
                      <X size={16} />
                    </button>
                  </>
                ) : confirmDelete === e.entry_id ? (
                  <>
                    <p className="flex-1 text-xs text-red-300">
                      {isItalian ? `Eliminare "${e.club_name}"?` : `Delete "${e.club_name}"?`}
                    </p>
                    <button
                      className="px-2.5 py-1 bg-red-500/15 border border-red-500/40 text-red-300 rounded-lg text-xs font-bold hover:bg-red-500/25 transition-colors disabled:opacity-40"
                      onClick={() => handleDelete(e.entry_id)}
                      disabled={saving}
                      data-testid="albo-legacy-confirm-delete-btn"
                    >
                      {isItalian ? 'Elimina' : 'Delete'}
                    </button>
                    <button
                      className="px-2.5 py-1 bg-white/5 border border-white/15 text-slate-300 rounded-lg text-xs hover:bg-white/10 transition-colors"
                      onClick={() => setConfirmDelete(null)}
                      data-testid="albo-legacy-abort-delete-btn"
                    >
                      {isItalian ? 'Annulla' : 'Cancel'}
                    </button>
                  </>
                ) : (
                  <>
                    <Trophy size={14} className="text-[#FFD700] shrink-0" />
                    <p className="flex-1 min-w-0 text-sm text-white truncate">{e.club_name}</p>
                    <span className="px-2 py-0.5 bg-[#FFD700]/10 border border-[#FFD700]/30 text-[#FFD700] rounded text-[11px] font-bold shrink-0">
                      {e.wins}×
                    </span>
                    <button
                      className="p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                      onClick={() => {
                        setEditId(e.entry_id);
                        setEditName(e.club_name);
                        setEditWins(e.wins);
                        setConfirmDelete(null);
                      }}
                      data-testid={`albo-legacy-edit-btn-${e.entry_id}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      className="p-1.5 text-red-400/70 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      onClick={() => {
                        setConfirmDelete(e.entry_id);
                        setEditId(null);
                      }}
                      data-testid={`albo-legacy-delete-btn-${e.entry_id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
